import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import NumberFormat from "react-number-format"
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Breadcrumb } from "../Breadcrumb"
import { Model } from "../../../models/model"
import { HeadRows } from "./HeadRows"
import { ItemsRows } from "./ItemsRows"
import "../EstadoResultado/estadoResultado.css"

export const CustomerTracking = () => {
    const money = useSelector(state => state.general.info)
    const [status, setStatus] = useState('por cobrar')
    const [refresh, setRefresh] = useState(0)
    const [report, setReport] = useState(null)
    const [search, setSearch] = useState('')
    const [horizon, setHorizon] = useState(3)
    const [showProjection, setShowProjection] = useState(false)
    const [openAll, setOpenAll] = useState(false)

    useEffect(() => {
        let active = true
        const load = async () => {
            const query = new URLSearchParams({ estado: status })
            const result = await Model.getAll({ url: `reports/customer-tracking?${query}` })
            if (active) setReport({ status, refresh, result })
        }
        load()
        return () => { active = false }
    }, [status, refresh])

    const loaded = report?.status === status && report?.refresh === refresh
    const result = loaded ? report.result : null
    const totals = result?.totales || {}
    const pending = Number(totals.por_cobrar_neto)
    const overdue = Number(totals.vencido_neto)
    const rows = [...(result?.data || [])].sort((a, b) =>
        Number(b.total_late) - Number(a.total_late) || Number(b.total_por_cobrar) - Number(a.total_por_cobrar))
    const top = rows.filter(row => Number(row.total_late) > 0).slice(0, 5).map(row => ({
        ...row, label: (row.customer || 'Sin nombre').toLocaleUpperCase('es'), overdue: Number(row.total_late),
    }))
    const months = showProjection ? (result?.months || []).slice(0, horizon) : []
    const visible = rows.filter(row => (row.customer || '').toLocaleLowerCase('es').includes(search.trim().toLocaleLowerCase('es')))
        .map(row => ({
            ...row,
            nextMonths: row.nextMonths?.slice(0, months.length),
            projects: row.projects?.map(project => ({ ...project, nextMonths: project.nextMonths?.slice(0, months.length) })),
        }))

    const amount = value => (
        <NumberFormat displayType="text" value={value}
            thousandSeparator={money?.local_separacion_miles || ','}
            decimalSeparator={money?.local_separacion_decimales || '.'}
            prefix={`${money?.local_simbolo || 'S/'} `} decimalScale={2} fixedDecimalScale />
    )

    return (
        <div className="p-container er-report">
            <div className="mt-3">
                <Breadcrumb routes={[
                    { id: 1, name: 'Reportes', link: '/reportes', active: false },
                    { id: 2, name: 'Pendiente por cobrar', link: '/reportes/ct', active: true },
                ]} />
            </div>
            <header className="er-heading">
                <div>
                    <span className="er-eyebrow">Reportes de clientes</span>
                    <h1 className="p-title">Pendiente por cobrar</h1>
                    <p>¿Cuánto falta cobrar, qué está vencido y a qué clientes debemos contactar primero?</p>
                </div>
                <div className="er-filters d-flex flex-wrap gap-2 align-items-end">
                    <div>
                        <label className="form-label" htmlFor="collection-status">Estado</label>
                        <select className="form-select" id="collection-status" value={status} onChange={event => setStatus(event.target.value)}>
                            <option value="por cobrar">Por cobrar</option>
                            <option value="cobrado">Cobrado</option>
                        </select>
                    </div>
                    <button type="button" className="btn p-btn-secondary" disabled={!loaded}
                        onClick={() => setRefresh(value => value + 1)}>Actualizar</button>
                </div>
            </header>

            {!loaded && <p role="status">Cargando reporte de cobranza…</p>}
            {loaded && !result && <p role="alert">No se pudo cargar el reporte. Intenta actualizarlo.</p>}
            {result && (
                <>
                    <p className="er-source mb-3">
                        Montos netos en moneda local. Por cobrar combina saldos de facturas y proyecciones pendientes.
                        Vencido se calcula según la fecha de proyección.
                    </p>
                    <div className="row g-2 mb-3">
                        {[
                            ['Por cobrar', amount(pending)],
                            ['Vencido según proyección', amount(overdue)],
                            ['% vencido sobre pendiente', pending > 0 && overdue >= 0 && overdue <= pending
                                ? new Intl.NumberFormat('es-PE', { style: 'percent', maximumFractionDigits: 1 }).format(overdue / pending) : '—'],
                            ['Cobrado', amount(totals.cobrado_neto)],
                        ].map(([label, value]) => (
                            <div className="col-6 col-lg-3" key={label}>
                                <div className="er-chart h-100 mb-0">
                                    <div className="er-source">{label}</div>
                                    <strong className="fs-5">{value}</strong>
                                </div>
                            </div>
                        ))}
                    </div>
                    {top.length > 0 && (
                        <figure className="er-chart">
                            <figcaption className="fw-bold mb-2">Clientes con mayor importe vencido según proyección</figcaption>
                            <div className="er-chart-scroll">
                                <ResponsiveContainer width="100%" minWidth={340} height={top.length * 36 + 12}>
                                    <BarChart layout="vertical" data={top}>
                                        <XAxis type="number" hide />
                                        <YAxis type="category" dataKey="label" width={155} tick={{ fontSize: 11 }} />
                                        <Tooltip formatter={value => amount(value)} />
                                        <Bar dataKey="overdue" name="Vencido" fill="#b02a37" maxBarSize={22} isAnimationActive={false} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <ul className="list-unstyled mb-0">
                                {top.map(row => <li key={row.id}><small>{row.label}</small> · {amount(row.overdue)}</li>)}
                            </ul>
                        </figure>
                    )}
                    {!rows.length ? <p role="status">No hay clientes para el estado seleccionado.</p> : (
                        <>
                            <div className="d-flex flex-wrap gap-3 align-items-end mb-2">
                                <div>
                                    <label className="form-label" htmlFor="collection-search">Buscar cliente</label>
                                    <input className="form-control" id="collection-search" type="search" value={search}
                                        onChange={event => setSearch(event.target.value)} />
                                </div>
                                <button type="button" className="btn p-btn-secondary" aria-pressed={showProjection}
                                    onClick={() => setShowProjection(value => !value)}>
                                    {showProjection ? 'Ocultar cobros proyectados' : 'Ver cobros proyectados'}
                                </button>
                                {showProjection && <div>
                                    <label className="form-label" htmlFor="collection-horizon">Horizonte</label>
                                    <select className="form-select" id="collection-horizon" value={horizon}
                                        onChange={event => setHorizon(Number(event.target.value))}>
                                        <option value={3}>3 meses</option>
                                        <option value={6}>6 meses</option>
                                        <option value={12}>12 meses</option>
                                    </select>
                                </div>}
                            </div>
                            {!visible.length ? <p role="status">No hay clientes que coincidan con la búsqueda.</p> : (
                                <div className="table-responsive er-table-wrap" tabIndex={0} role="region" aria-label="Cobranza por cliente y proyecto">
                                    <table className="table er-table mb-0">
                                        <thead><HeadRows months={months} openAll={openAll} setOpenAll={setOpenAll} /></thead>
                                        <tbody><ItemsRows data={visible} openAll={openAll} /></tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    )
}
